import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowRight, ArrowLeft, ShieldCheck, LifeBuoy,
  Sparkles, Zap, Lock,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

const SPARK = [
  { v: 34 }, { v: 41 }, { v: 38 }, { v: 47 }, { v: 55 },
  { v: 52 }, { v: 62 }, { v: 71 }, { v: 68 }, { v: 79 }, { v: 88 }, { v: 96 },
];

export default function Login() {
  const { login, verify2FA, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try { await import("@/lib/api").then((m) => m.default.post("/auth/logout")); }
    catch { /* pas de session résiduelle, on continue */ }
    const res = await login(email, password);
    setLoading(false);
    if (res.requires_2fa) {
      setChallenge({ challenge_token: res.challenge_token, email: res.email });
      return;
    }
    if (res.ok) {
      const s = res.session_data || await refresh();
      if (s?.scope === "enroll_2fa_only" || res?.session_data?.scope === "enroll_2fa_only") {
        navigate("/app/settings/security");
      } else {
        navigate("/app");
      }
    } else {
      setError(res.error);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await verify2FA(challenge.challenge_token, code.trim());
    setLoading(false);
    if (res.ok) navigate("/app");
    else setError(res.error);
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] bg-[#F8FAFC] antialiased"
      style={{ fontFamily: "'Plus Jakarta Sans', 'Manrope', system-ui, sans-serif" }}
      data-testid="login-page"
    >
      {/* ─── Left · Form ─── */}
      <div className="flex flex-col justify-between px-6 sm:px-12 lg:px-16 py-8 lg:py-10 relative">
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 -z-0 opacity-[0.35] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#E2E8F0 1px, transparent 1px), linear-gradient(90deg, #E2E8F0 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "radial-gradient(ellipse 60% 80% at 30% 40%, #000 30%, transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 60% 80% at 30% 40%, #000 30%, transparent 80%)",
          }}
        />

        <div className="relative flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2.5 group" data-testid="login-brand">
            <BrandLogo variant="icon" className="h-8 w-8" />
            <span className="font-bold tracking-tight text-[15px] text-[#0B0F17]">
              {APP_NAME}
            </span>
          </Link>
          <Link
            to="/welcome"
            data-testid="login-back-home"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0B0F17] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Retour au site
          </Link>
        </div>

        <div className="relative max-w-md w-full mx-auto lg:mx-0 py-10 lg:py-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1 ring-indigo-200 bg-indigo-50/80 mb-6">
            <Lock className="h-3 w-3 text-[#4F46E5]" />
            <span className="text-[11px] font-semibold text-[#4338CA] tracking-wide">
              Connexion sécurisée · 2FA supportée
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B0F17] leading-[1.1]">
            {challenge ? "Vérification en 2 étapes" : "Content de vous revoir."}
          </h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            {challenge
              ? `Ouvrez votre application d'authentification et saisissez le code à 6 chiffres généré pour votre compte.`
              : `Connectez-vous à votre espace ${APP_NAME} pour retrouver vos tableaux de bord, factures et prévisionnel de trésorerie.`}
          </p>

          <form
            onSubmit={challenge ? onVerify : onSubmit}
            className="mt-8 space-y-4"
            data-testid="login-form"
          >
            {error && (
              <Alert variant="destructive" data-testid="login-error" className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-900 text-sm">{error}</AlertDescription>
              </Alert>
            )}

            {challenge ? (
              <>
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600" data-testid="2fa-challenge-banner">
                  Compte&nbsp;: <span className="font-semibold text-[#0B0F17]">{challenge.email}</span>
                  <br />
                  Vous pouvez aussi utiliser un code de récupération à usage unique.
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tfa-code" className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                    Code à 6 chiffres
                  </Label>
                  <Input
                    id="tfa-code"
                    type="text"
                    autoFocus
                    required
                    inputMode="text"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="login-2fa-code-input"
                    className="h-12 tracking-[0.4em] text-center text-lg font-semibold border-slate-200 focus:border-[#4F46E5] focus:ring-[#4F46E5]/20"
                    maxLength={20}
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || code.trim().length < 6}
                  data-testid="login-2fa-verify-btn"
                  className={cn(
                    "group w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-sm font-semibold transition-all",
                    "bg-[#0B0F17] text-white hover:bg-[#1E293B] shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  {loading ? "Vérification…" : "Vérifier et se connecter"}
                  {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setChallenge(null); setCode(""); setError(""); }}
                  data-testid="login-2fa-cancel-btn"
                  className="w-full text-xs font-medium text-slate-500 hover:text-[#0B0F17] pt-1"
                >
                  ← Utiliser un autre compte
                </button>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                    Adresse email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="prenom.nom@entreprise.fr"
                    data-testid="login-email-input"
                    className="h-12 border-slate-200 focus:border-[#4F46E5] focus:ring-[#4F46E5]/20 placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                      Mot de passe
                    </Label>
                    <a
                      href="#"
                      className="text-[11px] font-semibold text-[#4F46E5] hover:text-[#4338CA]"
                      data-testid="login-forgot-link"
                    >
                      Mot de passe oublié ?
                    </a>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                    className="h-12 border-slate-200 focus:border-[#4F46E5] focus:ring-[#4F46E5]/20 placeholder:text-slate-300"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className={cn(
                    "group w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-sm font-semibold transition-all",
                    "bg-[#0B0F17] text-white hover:bg-[#1E293B] shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                  )}
                >
                  {loading ? "Connexion…" : "Se connecter"}
                  {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />}
                </button>
              </>
            )}
          </form>

          {!challenge && (
            <>
              <div className="mt-8 text-sm text-slate-600 text-center">
                Pas encore de compte ?{" "}
                <Link
                  to="/register?plan=business"
                  data-testid="link-to-register"
                  className="font-semibold text-[#4F46E5] hover:text-[#4338CA]"
                >
                  Créer un compte
                </Link>
              </div>

              <div className="mt-6 flex items-center gap-3 text-[11px] text-slate-400">
                <span className="h-px flex-1 bg-slate-200" />
                <span className="font-semibold uppercase tracking-widest">
                  Espace collaborateur
                </span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>

              <Link
                to="/staff/login"
                data-testid="link-to-staff"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 w-full justify-center rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-slate-300 hover:text-[#0B0F17] transition-colors"
              >
                <LifeBuoy className="h-3.5 w-3.5" />
                Accès portail Staff (interne {APP_NAME})
              </Link>
            </>
          )}
        </div>

        <div className="relative text-[11px] text-slate-400 flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3 text-emerald-500" />
            RGPD · Hébergé en France
          </span>
          <span className="text-slate-300">·</span>
          <span>SecNumCloud · ISO 27001</span>
          <span className="text-slate-300">·</span>
          <span>© {new Date().getFullYear()} {APP_NAME}</span>
        </div>
      </div>

      {/* ─── Right · Visual pane (hidden on mobile) ─── */}
      <div
        className="hidden lg:flex relative bg-[#0B0F17] text-white overflow-hidden flex-col justify-between p-12"
        data-testid="login-visual-pane"
      >
        {/* Grid + gradient background */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(#FFF 1px, transparent 1px), linear-gradient(90deg, #FFF 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 70% 90% at 70% 40%, #000 30%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 90% at 70% 40%, #000 30%, transparent 100%)",
          }}
        />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-indigo-500/25 blur-3xl" />
        <div className="absolute bottom-0 -left-24 h-96 w-96 rounded-full bg-emerald-500/15 blur-3xl" />

        {/* Top: tagline */}
        <div className="relative">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-indigo-300 flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Plateforme CFO
          </div>
          <div className="mt-4 text-3xl xl:text-4xl font-bold leading-[1.15] max-w-md">
            Vos KPIs vous attendent — clôture <span className="text-indigo-300">J+3</span>,
            trésorerie temps réel, board pack en 1 clic.
          </div>
        </div>

        {/* Middle: KPI mockup card */}
        <div className="relative w-full max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-5 shadow-2xl shadow-black/20">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  Trésorerie disponible
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span
                    className="text-3xl font-bold text-white tabular-nums"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    487&nbsp;213&nbsp;€
                  </span>
                  <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">
                    +12,4%
                  </span>
                </div>
              </div>
              <div className="h-8 w-8 rounded-lg bg-indigo-500/20 grid place-items-center">
                <Zap className="h-4 w-4 text-indigo-300" />
              </div>
            </div>
            <div className="h-24 mt-3 -mx-2">
              <ResponsiveContainer width="100%" height={96}>
                <AreaChart data={SPARK}>
                  <defs>
                    <linearGradient id="gLog" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#818CF8" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#818CF8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke="#818CF8" strokeWidth={2} fill="url(#gLog)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="pt-3 border-t border-white/10 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Runway</div>
                <div
                  className="text-white font-bold tabular-nums"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  18,4 mois
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">DSO</div>
                <div
                  className="text-white font-bold tabular-nums"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  32 jours
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom: quote */}
        <div className="relative">
          <div className="text-4xl leading-none text-indigo-400 mb-2 font-serif">&ldquo;</div>
          <blockquote className="text-base font-medium leading-relaxed max-w-md text-slate-100">
            Avant on clôturait à J+15 dans le stress. Aujourd'hui c'est J+3, sans erreur.
          </blockquote>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-400 to-emerald-400 grid place-items-center text-xs font-bold text-slate-900">
              CR
            </div>
            <div>
              <div className="text-sm font-semibold">Camille Rousseau</div>
              <div className="text-[11px] text-slate-400">DAF · Groupe industriel · 180 salariés</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
